import React from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Step,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Tooltip,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import { BsInfoCircle } from "react-icons/bs";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import templateApi from "../api/templateApi";
import ratingScaleApi from "../api/ratingScaleApi";
import ModulePageShell from "../components/common/ModulePageShell";
import OperationalPanel from "../components/common/OperationalPanel";
import PerformanceLocalNav from "../components/common/PerformanceLocalNav";
import StatusBadge from "../components/common/StatusBadge";
import { PageSkeleton } from "../components/common/PageState";
import TemplateSectionsEditor from "../components/templates/TemplateSectionsEditor";
import {
  createExcelDefaultSections,
  createDriverTemplatePreset,
  toBackendItemSource,
  toUiSource,
} from "../components/templates/templateSectionUtils";
import performanceQueryKeys from "../utils/queryKeys";
import { formatDate, recordId, unwrapDetail, unwrapRows } from "../utils/normalize";

const steps = [
  {
    title: "Basic information",
    description: "Set the template identity, dates, and weighting.",
  },
  {
    title: "Sections",
    description: "Build sections, items, sources, and input rules.",
  },
  {
    title: "Rating and scoring",
    description: "Choose the scale and scoring method.",
  },
  {
    title: "Workflow and rules",
    description: "Choose assessors and workflow requirements.",
  },
  {
    title: "Review and publish",
    description: "Validate the configuration and publish it.",
  },
];

const getIndianFinancialYearDates = (date = new Date()) => {
  const year = date.getFullYear();
  const financialYearStart = date.getMonth() >= 3 ? year : year - 1;
  return {
    from: `${financialYearStart}-04-01`,
    to: `${financialYearStart + 1}-03-31`,
  };
};

const defaultSection = {
  title: "",
  code: "",
  type: "GOALS",
  weightage: 0,
  calculationMethod: "WEIGHTED_ITEMS",
  itemSource: "MANUAL",
  isRequired: true,
  allowEmployeeRating: true,
  allowManagerRating: true,
  commentRequired: false,
  evidenceRequired: false,
  items: [],
};

const defaultValues = {
  name: "",
  code: "",
  description: "",
  effectiveFrom: "",
  effectiveTo: "",
  totalWeightage: 100,
  sectionWeightageMode: "STRICT_100",
  itemWeightageMode: "ABSOLUTE_SECTION_WEIGHT",
  ratingScaleId: "",
  sourceType: "EXCEL_SEED",
  sourceVersion: "PMS_FORMAT_V1",
  workflowConfiguration: {
    selfAssessmentRequired: true,
  },
  performanceCategories: [
    { code: "OUTSTANDING", label: "Outstanding", minScore: 90, maxScore: 100, minInclusive: true, maxInclusive: true, sequence: 1 },
    { code: "EXCEEDS_EXPECTATIONS", label: "Exceeds Expectations", minScore: 80, maxScore: 89.99, minInclusive: true, maxInclusive: true, sequence: 2 },
    { code: "MEETS_EXPECTATIONS", label: "Meets Expectations", minScore: 70, maxScore: 79.99, minInclusive: true, maxInclusive: true, sequence: 3 },
    { code: "NEEDS_IMPROVEMENT", label: "Needs Improvement", minScore: 60, maxScore: 69.99, minInclusive: true, maxInclusive: true, sequence: 4 },
    { code: "UNSATISFACTORY", label: "Unsatisfactory", minScore: 0, maxScore: 59.99, minInclusive: true, maxInclusive: true, sequence: 5 },
  ],
  developmentPlanConfiguration: {
    required: false,
    fields: ["developmentArea", "actionPlan", "supportRequired", "timeline"],
  },
  declarationConfiguration: {
    text: "",
  },
  sections: [
    ...createExcelDefaultSections(),
  ],
};

const getSectionDefaultsForType = (type) => {
  if (type === "GOALS") {
    return {
      itemSource: "EMPLOYEE_GOALS",
      calculationMethod: "GOAL_PROGRESS_WEIGHTED",
      minimumItemsRequired: 0,
    };
  }
  if (type === "COMPETENCY") {
    return {
      itemSource: "COMPETENCY_MASTER",
      calculationMethod: "COMPETENCY_WEIGHTED",
      minimumItemsRequired: 0,
    };
  }
  if (type === "CUSTOM") {
    return {
      itemSource: "MANUAL",
      calculationMethod: "WEIGHTED_ITEMS",
      minimumItemsRequired: 1,
    };
  }
  if (type === "DEVELOPMENT") {
    return {
      itemSource: "MANAGER_ASSESSMENT",
      calculationMethod: "DIRECT_SECTION_SCORE",
      minimumItemsRequired: 0,
    };
  }
  return {};
};

const normalizeCalculationMethod = (section = {}) => {
  if (
    section.calculationMethod === "WEIGHTED_AVERAGE" &&
    section.type === "CUSTOM" &&
    !section.items?.length
  ) {
    return "DIRECT_SECTION_SCORE";
  }
  if (section.calculationMethod === "WEIGHTED_AVERAGE") return "WEIGHTED_ITEMS";
  if (section.calculationMethod === "AVERAGE") return "AVERAGE_RATING";
  if (section.calculationMethod === "SUM") return "SUM_WEIGHTED_SCORES";
  return section.calculationMethod || defaultSection.calculationMethod;
};

const normalizeSection = (section = {}, index = 0) => {
  const normalizedType = section.type === "GOAL" ? "GOALS" : section.type || defaultSection.type;
  const typeDefaults = getSectionDefaultsForType(normalizedType);
  const source = section.itemSource || (section.source === "TEMPLATE" ? "MANUAL" : section.source);
  const shouldUseTypeSource =
    !source ||
    (normalizedType === "CUSTOM" && ["EMPLOYEE_GOALS", "COMPETENCY_MASTER", "360_CAMPAIGN"].includes(source)) ||
    (normalizedType === "GOALS" && source === "MANUAL") ||
    (normalizedType === "COMPETENCY" && source === "MANUAL");

  return {
    ...defaultSection,
    ...typeDefaults,
    ...section,
    sequence: Number.isFinite(Number(section.sequence)) ? Number(section.sequence) : index,
    type: normalizedType,
    calculationMethod: shouldUseTypeSource ? typeDefaults.calculationMethod || normalizeCalculationMethod(section) : normalizeCalculationMethod(section),
    uiItemSource: section.uiItemSource || toUiSource(section),
    itemSource: toBackendItemSource(section.uiItemSource || (shouldUseTypeSource ? typeDefaults.itemSource || defaultSection.itemSource : source)),
    minimumItemsRequired: section.minimumItemsRequired ?? typeDefaults.minimumItemsRequired ?? 0,
    isRequired: section.isRequired ?? section.mandatory ?? section.required ?? defaultSection.isRequired,
    mandatory: section.mandatory ?? section.isRequired ?? section.required ?? defaultSection.isRequired,
    allowComments: section.allowComments ?? true,
    commentRequired: section.commentRequired ?? section.commentsRequired ?? defaultSection.commentRequired,
    evidenceRequired: section.evidenceRequired ?? false,
  };
};

const normalizeTemplateForm = (template) => ({
  ...defaultValues,
  ...template,
  ratingScaleId: recordId(template?.ratingScaleId || template?.ratingScale) || template?.ratingScaleId || "",
  developmentPlanConfiguration: {
    ...defaultValues.developmentPlanConfiguration,
    ...(template?.developmentPlanConfiguration || {}),
    required: template?.developmentPlanConfiguration?.required ?? template?.developmentPlanRequired ?? defaultValues.developmentPlanConfiguration.required,
  },
  declarationConfiguration: {
    ...defaultValues.declarationConfiguration,
    ...(template?.declarationConfiguration || {}),
    text: template?.declarationConfiguration?.text ?? template?.declarationText ?? defaultValues.declarationConfiguration.text,
  },
  workflowConfiguration: {
    ...defaultValues.workflowConfiguration,
    ...(template?.workflowConfiguration || {}),
    selfAssessmentRequired:
      template?.workflowConfiguration?.selfAssessmentRequired !== false,
  },
  sections: Array.isArray(template?.sections) && template.sections.length
    ? template.sections.map(normalizeSection)
    : defaultValues.sections.map(normalizeSection),
});

const buildTemplatePayload = (payload, { includeSourceFields = false } = {}) => {
  const {
    name,
    code,
    description,
    version,
    effectiveFrom,
    effectiveTo,
    ratingScaleId,
    totalWeightage,
    sectionWeightageMode,
    itemWeightageMode,
    performanceCategories,
    workflowConfiguration,
    developmentPlanConfiguration,
    commentFieldConfiguration,
    declarationConfiguration,
    incrementConfiguration,
    promotionConfiguration,
    applicableRoles,
    status,
    sourceType,
    sourceVersion,
    developmentPlanRequired,
    declarationText,
    sections = [],
  } = payload;

  const templatePayload = {
    name,
    code,
    description,
    version,
    effectiveFrom: effectiveFrom || null,
    effectiveTo: effectiveTo || null,
    ratingScaleId: ratingScaleId || undefined,
    totalWeightage: Number(totalWeightage) || defaultValues.totalWeightage,
    sectionWeightageMode: sectionWeightageMode || defaultValues.sectionWeightageMode,
    itemWeightageMode: itemWeightageMode || "ABSOLUTE_SECTION_WEIGHT",
    performanceCategories: performanceCategories || [],
    workflowConfiguration: {
      ...(workflowConfiguration || {}),
      selfAssessmentRequired: workflowConfiguration?.selfAssessmentRequired !== false,
    },
    commentFieldConfiguration: commentFieldConfiguration || {},
    incrementConfiguration: incrementConfiguration || {},
    promotionConfiguration: promotionConfiguration || {},
    applicableRoles: applicableRoles || [],
    status: status === "DRAFT" ? status : undefined,
    sections: sections.map((section, index) => {
      const normalized = normalizeSection(section, index);
      const sectionPayload = { ...normalized };
      delete sectionPayload.fieldKey;
      delete sectionPayload.id;
      delete sectionPayload._id;
      delete sectionPayload.uiItemSource;
      return {
        ...sectionPayload,
        itemSource: toBackendItemSource(normalized.uiItemSource || toUiSource(normalized)),
        items: (normalized.items || []).map((item, itemIndex) => {
          const itemPayload = { ...item };
          delete itemPayload.fieldKey;
          delete itemPayload.id;
          delete itemPayload._id;
          return {
            ...itemPayload,
            sequence: itemIndex + 1,
            itemId: item.itemId || item.itemCode || `${normalized.code || "ITEM"}_${Date.now()}_${itemIndex}`,
            itemCode: item.itemCode || item.itemId || `${normalized.code || "ITEM"}_${itemIndex + 1}`,
            sourceType: item.sourceType || "STATIC_TEMPLATE_ITEMS",
            commentRequired: item.commentRequired ?? item.commentRule === "REQUIRED",
            evidenceRequired: item.evidenceRequired ?? item.evidenceRule === "REQUIRED",
          };
        }),
      };
    }),
    developmentPlanConfiguration: {
      ...(developmentPlanConfiguration || {}),
      required: developmentPlanConfiguration?.required ?? developmentPlanRequired ?? false,
    },
    declarationConfiguration: {
      ...(declarationConfiguration || {}),
      text: declarationConfiguration?.text ?? declarationText ?? "",
    },
  };

  if (includeSourceFields) {
    templatePayload.sourceType = sourceType || defaultValues.sourceType;
    templatePayload.sourceVersion = sourceVersion || defaultValues.sourceVersion;
  }

  return Object.fromEntries(
    Object.entries(templatePayload).filter(([, value]) => value !== undefined),
  );
};

const formatApiError = (error) => {
  const response = error?.response?.data;
  const errors = response?.errors;
  if (Array.isArray(errors) && errors.length) {
    return errors
      .map((item) => (typeof item === "string" ? item : item?.message))
      .filter(Boolean)
      .join(" ");
  }
  return response?.message || error.message;
};

const PerformanceTemplateBuilderPage = ({ mode = "create" }) => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = React.useState(0);
  const subtleBg = useColorModeValue("gray.50", "gray.900");
  const mutedText = useColorModeValue("gray.600", "gray.400");

  const templateQuery = useQuery({
    queryKey: performanceQueryKeys.templates.detail(templateId),
    queryFn: () => templateApi.getPerformanceTemplate(templateId),
    enabled: mode === "edit" && Boolean(templateId),
  });

  const ratingScalesQuery = useQuery({
    queryKey: performanceQueryKeys.ratingScales.list({ limit: 100, status: "ACTIVE" }),
    queryFn: () => ratingScaleApi.getPerformanceRatingScales({ limit: 100, status: "ACTIVE" }),
  });

  const form = useForm({ defaultValues });
  const { register, handleSubmit, reset, setValue, watch, formState } = form;
  const values = watch();
  const sections = watch("sections") || [];
  const totalWeightage = sections.reduce((sum, section) => sum + (Number(section.weightage) || 0), 0);
  const ratingScales = unwrapRows(ratingScalesQuery.data);
  const template = unwrapDetail(templateQuery.data);

  React.useEffect(() => {
    if (mode === "edit" && template) {
      reset(normalizeTemplateForm(template));
    }
  }, [mode, reset, template]);

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      mode === "edit"
        ? templateApi.updatePerformanceTemplate(templateId, payload)
        : templateApi.createPerformanceTemplate(payload),
    onSuccess: (response) => {
      toast({ title: "Template draft saved", status: "success", duration: 2000 });
      queryClient.invalidateQueries({ queryKey: performanceQueryKeys.templates.all });
      const saved = unwrapDetail(response);
      if (mode === "create" && recordId(saved)) {
        navigate(`/performance/pms-format/templates/${recordId(saved)}/edit`, { replace: true });
      }
    },
    onError: (error) =>
      toast({
        title: "Unable to save template",
        description: formatApiError(error),
        status: "error",
      }),
  });

  const validateMutation = useMutation({
    mutationFn: () => templateApi.validatePerformanceTemplate(templateId),
    onSuccess: () => toast({ title: "Template validation completed", status: "success", duration: 2000 }),
    onError: (error) =>
      toast({
        title: "Template validation failed",
        description: formatApiError(error),
        status: "error",
      }),
  });

  const publishMutation = useMutation({
    mutationFn: () => templateApi.publishPerformanceTemplate(templateId),
    onSuccess: () => {
      toast({ title: "Template published", status: "success", duration: 2000 });
      navigate("/performance/pms-format/templates");
    },
    onError: (error) =>
      toast({
        title: "Unable to publish template",
        description: formatApiError(error),
        status: "error",
      }),
  });

  const handleSave = (payload) => {
    const preparedPayload = buildTemplatePayload(payload, {
      includeSourceFields: mode !== "edit",
    });

    if (!preparedPayload.ratingScaleId) {
      toast({
        title: "Select a rating scale",
        description: "Templates need a rating scale before they can be saved.",
        status: "warning",
      });
      setActiveStep(2);
      return;
    }

    saveMutation.mutate(preparedPayload);
  };

  const applyIndianFinancialYear = () => {
    const { from, to } = getIndianFinancialYearDates();
    setValue("effectiveFrom", from, { shouldDirty: true, shouldValidate: true });
    setValue("effectiveTo", to, { shouldDirty: true, shouldValidate: true });
  };

  const applyDriverTemplatePreset = () => {
    const preset = createDriverTemplatePreset();
    reset({
      ...values,
      ...preset,
      ratingScaleId: values.ratingScaleId || "",
      performanceCategories: values.performanceCategories?.length
        ? values.performanceCategories
        : defaultValues.performanceCategories,
    });
    setActiveStep(0);
    toast({
      title: "Driver template preset loaded",
      description: "Review the driver-specific sections, select a rating scale, and save the template.",
      status: "success",
      duration: 3000,
    });
  };

  if (templateQuery.isLoading) {
    return (
      <ModulePageShell title="Template builder" description="Configure appraisal format and scoring rules.">
        <PerformanceLocalNav />
        <OperationalPanel>
          <PageSkeleton rows={6} />
        </OperationalPanel>
      </ModulePageShell>
    );
  }

  return (
    <ModulePageShell
      title="Template builder"
      description="Configure appraisal format and scoring rules."
      actions={
        <Stack direction={{ base: "column", sm: "row" }} align={{ base: "stretch", sm: "center" }} spacing={2} width={{ base: "100%", md: "auto" }}>
          <Text fontSize="xs" color={mutedText}>
            {formState.isDirty ? "Unsaved changes" : "Saved draft state"}
          </Text>
          <Button size="sm" variant="outline" onClick={handleSubmit(handleSave)} isLoading={saveMutation.isPending}>
            Save draft
          </Button>
          <Button size="sm" variant="outline" isDisabled={!templateId} onClick={() => navigate(`/performance/pms-format/templates/${templateId}`)}>
            Preview
          </Button>
          <Button size="sm" variant="outline" isDisabled={!templateId} onClick={() => validateMutation.mutate()} isLoading={validateMutation.isPending}>
            Validate
          </Button>
        </Stack>
      }
    >
      <PerformanceLocalNav />
      <form onSubmit={handleSubmit(handleSave)}>
        <VStack spacing={4} align="stretch">
          <OperationalPanel>
            <Flex justify="space-between" align={{ base: "stretch", md: "center" }} direction={{ base: "column", md: "row" }} gap={3}>
              <Box minW={0}>
                <HStack wrap="wrap">
                  <Text fontSize="lg" fontWeight="semibold" wordBreak="break-word">
                    {values.name || "Untitled appraisal template"}
                  </Text>
                  <StatusBadge value={values.status || "DRAFT"} />
                  <Badge colorScheme={totalWeightage === 100 ? "green" : "orange"}>
                    {totalWeightage}% weightage
                  </Badge>
                </HStack>
                <Text mt={1} fontSize="sm" color={mutedText}>
                  Version {values.version || 1} · Effective from {formatDate(values.effectiveFrom)}
                </Text>
              </Box>
              <Button size="sm" colorScheme="blue" type="submit" isLoading={saveMutation.isPending} width={{ base: "100%", md: "auto" }}>
                Save draft
              </Button>
            </Flex>
          </OperationalPanel>

          <OperationalPanel minW={0} pb={{ base: 4, md: 4 }}>
            <Stepper
              size="sm"
              index={activeStep}
              colorScheme="blue"
              orientation={{ base: "vertical", md: "horizontal" }}
              gap={{ base: 2, md: 0 }}
              sx={{
                "& .chakra-step__separator": {
                  alignSelf: "center",
                  height: "2px",
                  minHeight: "2px",
                  border: 0,
                  borderRadius: "full",
                  background: "gray.200",
                },
                "& .chakra-step[data-status=complete] .chakra-step__separator": {
                  background: "blue.500",
                },
                "& .chakra-step[data-status=active] .chakra-step__separator": {
                  background: "gray.200",
                },
                "& .chakra-step__indicator": {
                  position: "relative",
                  zIndex: 1,
                  background: "white",
                },
              }}
            >
              {steps.map((step, index) => (
                <Step key={step.title} aria-current={index === activeStep ? "step" : undefined} onClick={() => setActiveStep(index)} cursor="pointer">
                  <StepIndicator>
                    <StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<StepNumber />} />
                  </StepIndicator>
                  <Box flexShrink="1" minW={0} display="block">
                    <StepTitle>
                      <HStack spacing={1}>
                        <Text>{step.title}</Text>
                        <Tooltip label={step.description} hasArrow placement="top" openDelay={150} closeDelay={100}>
                          <IconButton
                            type="button"
                            aria-label={`Why ${step.title} is needed`}
                            icon={<BsInfoCircle />}
                            size="xs"
                            variant="ghost"
                          />
                        </Tooltip>
                      </HStack>
                    </StepTitle>
                  </Box>
                  <StepSeparator />
                </Step>
              ))}
            </Stepper>
          </OperationalPanel>

          {activeStep === 0 ? (
            <OperationalPanel>
              <Flex
                justify="space-between"
                align={{ base: "stretch", sm: "center" }}
                direction={{ base: "column", sm: "row" }}
                gap={3}
                mb={4}
              >
                <Box>
                  <Text fontWeight="semibold">Template basics</Text>
                  <Text fontSize="sm" color={mutedText}>
                    Start with the ready-made Driver appraisal structure.
                  </Text>
                </Box>
                <Button type="button" size="sm" variant="outline" onClick={applyDriverTemplatePreset}>
                  Load Driver preset
                </Button>
              </Flex>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Template name</FormLabel>
                  <Input size="sm" {...register("name", { required: true })} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Code</FormLabel>
                  <Input size="sm" {...register("code", { required: true })} />
                </FormControl>
                <FormControl gridColumn={{ md: "1 / -1" }}>
                  <FormLabel>Description</FormLabel>
                  <Textarea size="sm" {...register("description")} />
                </FormControl>
                <FormControl>
                  <Flex justify="space-between" align="center" gap={2}>
                    <FormLabel mb={0}>Effective from</FormLabel>
                    <Button type="button" size="xs" variant="outline" onClick={applyIndianFinancialYear}>
                      India FY
                    </Button>
                  </Flex>
                  <Input mt={2} size="sm" type="date" {...register("effectiveFrom")} />
                </FormControl>
                <FormControl>
                  <FormLabel>Effective to</FormLabel>
                  <Input size="sm" type="date" {...register("effectiveTo")} />
                </FormControl>
                <FormControl>
                  <FormLabel>Total weightage</FormLabel>
                  <Input size="sm" type="number" {...register("totalWeightage", { valueAsNumber: true })} />
                </FormControl>
                <FormControl>
                  <FormLabel>Item-weightage mode</FormLabel>
                  <Select size="sm" {...register("itemWeightageMode")}>
                    <option value="RELATIVE_PERCENTAGE">Relative percentage</option>
                    <option value="ABSOLUTE_SECTION_WEIGHT">Absolute section weight</option>
                    <option value="EQUAL_DISTRIBUTION">Equal distribution</option>
                  </Select>
                </FormControl>
              </SimpleGrid>
            </OperationalPanel>
          ) : null}

          {activeStep === 1 ? (
            <OperationalPanel>
              <TemplateSectionsEditor
                control={form.control}
                register={register}
                setValue={setValue}
                formState={formState}
                itemWeightageMode={values.itemWeightageMode}
              />
            </OperationalPanel>
          ) : null}

          {activeStep === 2 ? (
            <OperationalPanel>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Rating scale</FormLabel>
                  <Select size="sm" placeholder="Select rating scale" {...register("ratingScaleId", { required: true })}>
                    {ratingScales.map((scale) => (
                      <option key={recordId(scale)} value={recordId(scale)}>
                        {scale.name || scale.code}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid>
              {ratingScalesQuery.isError ? (
                <Box mt={3} bg="red.50" borderRadius="md" p={3}>
                  <Text fontSize="sm" color="red.700">
                    Unable to load rating scales. Please refresh or check your PMS rating-scale permissions.
                  </Text>
                </Box>
              ) : !ratingScalesQuery.isLoading && ratingScales.length === 0 ? (
                <Box mt={3} bg={subtleBg} borderRadius="md" p={3}>
                  <Flex justify="space-between" align={{ base: "stretch", sm: "center" }} direction={{ base: "column", sm: "row" }} gap={3}>
                    <Text fontSize="sm" color={mutedText}>
                      No active rating scales are available. Create or activate one before saving this template.
                    </Text>
                    <Button size="sm" variant="outline" onClick={() => navigate("/performance/pms-format/rating-scales/create")}>
                      Create scale
                    </Button>
                  </Flex>
                </Box>
              ) : null}
              <Box mt={4} bg={subtleBg} borderRadius="md" p={3}>
                <Text fontSize="sm" fontWeight="semibold">
                  Estimated scoring preview
                </Text>
                <Text mt={1} fontSize="sm" color={mutedText}>
                  Section scores are weighted to {totalWeightage}%. Backend calculation remains authoritative after validation.
                </Text>
              </Box>
            </OperationalPanel>
          ) : null}

          {activeStep === 3 ? (
            <OperationalPanel>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Checkbox {...register("developmentPlanConfiguration.required")}>Require development plan</Checkbox>
                <Checkbox {...register("workflowConfiguration.selfAssessmentRequired")}>
                  Require employee self-assessment
                </Checkbox>
                <FormControl>
                  <FormLabel>Declaration text</FormLabel>
                  <Textarea size="sm" {...register("declarationConfiguration.text")} />
                </FormControl>
              </SimpleGrid>
              <Box mt={4} bg={subtleBg} borderRadius="md" p={3}>
                <Text fontSize="sm" fontWeight="semibold">
                  {values.workflowConfiguration?.selfAssessmentRequired !== false
                    ? "Employee self-assessment is required before manager review."
                    : "Manager review starts immediately; employee self-assessment is skipped."}
                </Text>
              </Box>
              <TableContainer mt={4} maxW="100%" overflowX="auto">
                <Table size="sm" minW={{ base: "520px", md: "auto" }}>
                  <Thead>
                    <Tr>
                      <Th>Workflow stage</Th>
                      <Th>Actor</Th>
                      <Th>Rule</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(values.workflowConfiguration?.selfAssessmentRequired !== false
                      ? ["Self-assessment", "Manager assessment", "Calibration", "Finalization", "Acknowledgement"]
                      : ["Manager assessment", "Calibration", "Finalization", "Acknowledgement"]
                    ).map((stage) => (
                      <Tr key={stage}>
                        <Td>{stage}</Td>
                        <Td>{stage === "Self-assessment" || stage === "Acknowledgement" ? "Employee" : "Configured approver"}</Td>
                        <Td>Driven by tenant workflow configuration</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </OperationalPanel>
          ) : null}

          {activeStep === 4 ? (
            <OperationalPanel>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Box>
                  <Text fontSize="sm" color={mutedText}>Template</Text>
                  <Text fontWeight="semibold">{values.name || "Untitled"}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color={mutedText}>Sections</Text>
                  <Text fontWeight="semibold">{sections.length}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color={mutedText}>Validation</Text>
                  <StatusBadge value={totalWeightage === 100 ? "VALID" : "INVALID"} />
                </Box>
              </SimpleGrid>
              <Stack mt={4} direction={{ base: "column", sm: "row" }} justify="flex-end" align={{ base: "stretch", sm: "center" }} spacing={2}>
                <Button size="sm" variant="outline" type="submit" isLoading={saveMutation.isPending}>
                  Save draft
                </Button>
                <Button size="sm" variant="outline" isDisabled={!templateId} onClick={() => validateMutation.mutate()} isLoading={validateMutation.isPending}>
                  Validate
                </Button>
                <Button size="sm" colorScheme="blue" isDisabled={!templateId || totalWeightage !== 100} onClick={() => publishMutation.mutate()} isLoading={publishMutation.isPending}>
                  Publish
                </Button>
              </Stack>
            </OperationalPanel>
          ) : null}

          <Flex justify="space-between" direction={{ base: "column-reverse", sm: "row" }} gap={3}>
            <Button size="sm" variant="outline" isDisabled={activeStep === 0} onClick={() => setActiveStep((step) => step - 1)} width={{ base: "100%", sm: "auto" }}>
              Back
            </Button>
            <Button size="sm" colorScheme="blue" isDisabled={activeStep === steps.length - 1} onClick={() => setActiveStep((step) => step + 1)} width={{ base: "100%", sm: "auto" }}>
              Next
            </Button>
          </Flex>
        </VStack>
      </form>

    </ModulePageShell>
  );
};

export default PerformanceTemplateBuilderPage;
